import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';

import { UnsubscribeService } from '@webgate/angular-azure-core';
import { ManualTriggersService } from './manual-triggers.service';
import {
  InvalidUserIdMatchWithAllMatches,
  GraphMatchOptions,
  NameMatchingStrategy
} from '../../../models/manualGraphMatches';
import { MigrationResult } from '../../../models/notesToAzureUserMigration';
import { ConfirmDialogComponent } from './confirm-dialog.component';
import { PersonSelectComponent } from '../../person-select-dialog/person-select.component';
import { UserService } from '../../../services/user.service';

@Component({
  selector: 'app-manual-graph-matches',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTableModule,
    MatSnackBarModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatTooltipModule
  ],
  templateUrl: './manual-graph-matches.component.html',
  styleUrl: './manual-graph-matches.component.scss'
})
export class ManualGraphMatchesComponent {
  private manualTriggersService = inject(ManualTriggersService);
  private snackBar = inject(MatSnackBar);
  private unsubscribeService = inject(UnsubscribeService);
  private dialog = inject(MatDialog);
  private userService = inject(UserService);

  matches: InvalidUserIdMatchWithAllMatches[] = [];
  filteredMatches: InvalidUserIdMatchWithAllMatches[] = [];
  isLoading = false;
  searchTerm = '';
  filterMatchCount: 'all' | '0' | '2+' = 'all';

  displayedColumns: string[] = [
    'invalidUserId',
    'extractedDisplayName',
    'matchCount',
    'recommendationCount',
    'auditCount',
    'totalCount',
    'actions'
  ];

  // Tracking für angewendete Matches
  applyingMatches = new Set<string>(); // Set von invalidUserId
  appliedMatches = new Set<string>(); // Set von invalidUserId
  hasLoadedOnce = false; // Track ob bereits einmal geladen wurde

  // Graph Match Options
  graphMatchOptions: GraphMatchOptions = {
    nameMatchingStrategy: NameMatchingStrategy.Normalized,
    emailDomainFilter: ''
  };

  loadMatches(): void {
    this.isLoading = true;

    const options: GraphMatchOptions = {
      nameMatchingStrategy: this.graphMatchOptions.nameMatchingStrategy,
      emailDomainFilter: this.graphMatchOptions.emailDomainFilter?.trim() || undefined
    };

    this.manualTriggersService
      .getManualGraphMatches(options)
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: (matches) => {
          this.matches = matches;
          this.filterMatches();
          this.isLoading = false;
          this.hasLoadedOnce = true;
        },
        error: (error) => {
          this.isLoading = false;
          this.hasLoadedOnce = true;
          this.showError('Fehler beim Laden der Matches: ' + this.extractErrorMessage(error));
        }
      });
  }

  filterMatches(): void {
    let filtered = [...this.matches];

    // Filter nach MatchCount
    if (this.filterMatchCount === '0') {
      filtered = filtered.filter((m) => m.matchCount === 0);
    } else if (this.filterMatchCount === '2+') {
      filtered = filtered.filter((m) => m.matchCount >= 2);
    }

    // Filter nach Suchbegriff
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.invalidUserId.toLowerCase().includes(searchLower) ||
          m.extractedDisplayName.toLowerCase().includes(searchLower)
      );
    }

    this.filteredMatches = filtered;
  }

  openUserSearch(match: InvalidUserIdMatchWithAllMatches): void {
    this.dialog
      .open(PersonSelectComponent, {
        width: '800px',
        data: {
          dialogTitle: `Azure User für "${match.extractedDisplayName}" suchen`,
          singleSelect: true,
          currentPeople: []
        }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.save && result.id) {
          match.selectedAzureUserId = result.id;
        }
      });
  }

  getSelectedUserName(azureUserId: string): string {
    const user = this.userService.getAllUsers().find((u) => u.id === azureUserId);
    return user?.displayName || azureUserId;
  }

  applyMatch(match: InvalidUserIdMatchWithAllMatches): void {
    const azureUserId = match.selectedAzureUserId;
    if (!azureUserId) {
      this.showError('Bitte wählen Sie einen Azure User aus');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        title: 'Graph API Match anwenden',
        message:
          `Möchten Sie den Match wirklich anwenden?\n\n` +
          `Invalid User ID: ${match.invalidUserId}\n` +
          `Azure User ID: ${azureUserId}\n\n` +
          `Dies wird ${match.totalCount} Einträge aktualisieren (${match.recommendationCount} Recommendations, ${match.auditCount} Audits).\n\n` +
          `Diese Aktion kann nicht rückgängig gemacht werden.`,
        confirmText: 'Ja, Match anwenden',
        cancelText: 'Abbrechen'
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.executeApplyMatch(match, azureUserId);
      }
    });
  }

  private executeApplyMatch(match: InvalidUserIdMatchWithAllMatches, azureUserId: string): void {
    this.applyingMatches.add(match.invalidUserId);

    this.manualTriggersService
      .applySingleGraphMatch({
        invalidUserId: match.invalidUserId,
        azureUserId: azureUserId
      })
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe({
        next: (result: MigrationResult) => {
          this.applyingMatches.delete(match.invalidUserId);
          this.appliedMatches.add(match.invalidUserId);

          const message = `Match angewendet: ${result.updatedRecommendations}/${result.totalRecommendations} Recommendations, ${result.updatedAudits}/${result.totalAudits} Audits aktualisiert`;
          this.snackBar.open(message, 'Schließen', {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'success-snackbar'
          });

          // Entferne den Match aus der Liste (optional - oder markiere als angewendet)
          // this.matches = this.matches.filter(m => m.invalidUserId !== match.invalidUserId);
          // this.filterMatches();
        },
        error: (error) => {
          this.applyingMatches.delete(match.invalidUserId);
          this.showError('Fehler beim Anwenden des Matches: ' + this.extractErrorMessage(error));
        }
      });
  }

  isApplying(match: InvalidUserIdMatchWithAllMatches): boolean {
    return this.applyingMatches.has(match.invalidUserId);
  }

  isApplied(match: InvalidUserIdMatchWithAllMatches): boolean {
    return this.appliedMatches.has(match.invalidUserId);
  }

  canApply(match: InvalidUserIdMatchWithAllMatches): boolean {
    return !!match.selectedAzureUserId && !this.isApplying(match) && !this.isApplied(match);
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Schließen', {
      duration: 8000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'error-snackbar'
    });
  }

  private extractErrorMessage(error: any): string {
    if (error?.error) {
      if (typeof error.error === 'string') {
        return error.error;
      }
      if (error.error.message) {
        return error.error.message;
      }
      if (error.error.title) {
        return error.error.title;
      }
    }
    if (error?.message) {
      return error.message;
    }
    return 'Ein unbekannter Fehler ist aufgetreten';
  }
}
